<template>
  <div>
    <v-row dense class="no-print">
      <v-col cols="12" sm="6" md="2">
        <v-select
          v-model="periodType"
          :items="periodTypes"
          label="Periode"
          outlined
          dense
        ></v-select>
      </v-col>

      <template v-if="periodType === 'bulan'">
        <v-col cols="12" sm="6" md="2">
          <v-text-field
            v-model="bulan"
            type="month"
            label="Bulan"
            outlined
            dense
          ></v-text-field>
        </v-col>
      </template>

      <template v-else-if="periodType === 'triwulan'">
        <v-col cols="6" sm="3" md="2">
          <v-select
            v-model="triwulan"
            :items="triwulanOptions"
            label="Triwulan"
            outlined
            dense
          ></v-select>
        </v-col>
        <v-col cols="6" sm="3" md="2">
          <v-text-field
            v-model.number="tahun"
            type="number"
            label="Tahun"
            outlined
            dense
          ></v-text-field>
        </v-col>
      </template>

      <template v-else>
        <v-col cols="12" sm="6" md="2">
          <v-text-field
            v-model="rentangAwal"
            type="date"
            label="Tgl TTPB Dari"
            outlined
            clearable
            dense
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="6" md="2">
          <v-text-field
            v-model="rentangAkhir"
            type="date"
            label="Tgl TTPB Sampai"
            outlined
            clearable
            dense
          ></v-text-field>
        </v-col>
      </template>

      <v-col cols="12" sm="6" md="3">
        <v-autocomplete
          v-model="formData.marketing"
          :items="marketingOptions"
          :loading="state.loadingMarketing"
          :disabled="state.loading"
          label="Marketing"
          outlined
          dense
        ></v-autocomplete>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-select
          v-model="formData.status_bonus"
          :items="statusBonusOptions"
          :disabled="state.loading"
          label="Status Insentif"
          outlined
          dense
        ></v-select>
      </v-col>
      <v-col cols="12" sm="6" md="2">
        <v-btn
          color="primary"
          @click="fetchLaporan"
          :loading="state.loading"
          :disabled="!periode.awal || !periode.akhir || state.loading"
        >
          Cari
        </v-btn>
      </v-col>
    </v-row>

    <v-row v-if="state.loading" align-content="center" justify="center">
      <v-col class="text-subtitle-1 text-center" cols="12">Loading ...</v-col>
      <v-col cols="6">
        <v-progress-linear indeterminate rounded height="6"></v-progress-linear>
      </v-col>
    </v-row>

    <p v-if="state.searched && state.noData && !state.loading">No Data</p>

    <main
      class="report"
      v-if="state.searched && !state.loading"
      id="section-print"
    >
      <h2 class="report-title">LAPORAN MARKETING</h2>
      <p>
        Periode TTPB: {{ formatDate(reportPeriode.awal) }} s/d
        {{ formatDate(reportPeriode.akhir) }}<br />
        Marketing: {{ reportMarketingName }}<br />
        Status Insentif: {{ statusBonusLabel }}<br />
        <small>
          Hanya transaksi yang sudah lunas. Pelunasan di bawah 1 bulan dan di
          atas 1 bulan dihitung dari tgl TTPB (barang diterima penerima) sampai
          tgl lunas nota.
        </small>
      </p>

      <div v-for="m in data" :key="'d' + m.marketing" class="mt-6 detail">
        <h3>Marketing: {{ m.nama }}</h3>

        <template v-for="g in groups">
          <h4 :key="m.marketing + g.key + 'h'" class="mt-3">{{ g.label }}</h4>
          <table
            v-if="itemsOf(m, g.key).length"
            :key="m.marketing + g.key + 't'"
            width="100%"
            class="tbl"
          >
            <thead>
              <tr>
                <th>No TTB</th>
                <th>Tgl TTPB</th>
                <th>No Nota</th>
                <th>Customer</th>
                <th>Tgl Lunas</th>
                <th>Hari</th>
                <th>Kubikasi (M3)</th>
                <th>Tonase (Ton)</th>
                <th class="text-right">Tagihan</th>
                <th v-if="showTglInsentif">Tgl Insentif</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="i in itemsOf(m, g.key)" :key="i.kodettb">
                <td>{{ i.nottb || i.kodettb }}</td>
                <td>{{ formatDate(i.tgl_ttpb) }}</td>
                <td>{{ i.nomor_nota || "-" }}</td>
                <td>{{ i.customer || "-" }}</td>
                <td>{{ formatDate(i.tgl_lunas) }}</td>
                <td class="text-center">{{ i.hari === null ? "-" : i.hari }}</td>
                <td class="text-center">{{ i.m3.toFixed(3) }}</td>
                <td class="text-center">{{ i.ton.toFixed(3) }}</td>
                <td class="text-right">{{ formatCurrency(i.tagihan) }}</td>
                <td v-if="showTglInsentif">
                  {{ i.bonus_id ? formatDate(i.tgl_bonus) : "Belum" }}
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td colspan="6"><b>Total</b></td>
                <td class="text-center">
                  <b>{{ sumOf(itemsOf(m, g.key), "m3").toFixed(3) }}</b>
                </td>
                <td class="text-center">
                  <b>{{ sumOf(itemsOf(m, g.key), "ton").toFixed(3) }}</b>
                </td>
                <td class="text-right">
                  <b>{{ formatCurrency(sumOf(itemsOf(m, g.key), "tagihan")) }}</b>
                </td>
                <td v-if="showTglInsentif"></td>
              </tr>
            </tfoot>
          </table>
          <p v-else :key="m.marketing + g.key + 'e'" class="mb-0">
            <small>Tidak ada data.</small>
          </p>
        </template>

        <h4 class="mt-4">Rekapitulasi</h4>
        <table class="tbl rekap">
          <thead>
            <tr>
              <th></th>
              <th>Kubikasi (M3)</th>
              <th>Tonase (Ton)</th>
              <th class="text-right">Tagihan</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in rekapRows" :key="r.key">
              <td :class="{ 'font-weight-bold': r.key === 'total' }">
                {{ r.label }}
              </td>
              <td class="text-center">{{ m[r.key].m3.toFixed(3) }}</td>
              <td class="text-center">{{ m[r.key].ton.toFixed(3) }}</td>
              <td class="text-right">{{ formatCurrency(m[r.key].tagihan) }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="data.length > 1" class="mt-6 detail">
        <h3>Rekapitulasi Seluruh Marketing</h3>
        <table class="tbl rekap">
          <thead>
            <tr>
              <th></th>
              <th>Kubikasi (M3)</th>
              <th>Tonase (Ton)</th>
              <th class="text-right">Tagihan</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in rekapRows" :key="r.key">
              <td :class="{ 'font-weight-bold': r.key === 'total' }">
                {{ r.label }}
              </td>
              <td class="text-center">{{ grand(r.key, "m3").toFixed(3) }}</td>
              <td class="text-center">{{ grand(r.key, "ton").toFixed(3) }}</td>
              <td class="text-right">
                {{ formatCurrency(grand(r.key, "tagihan")) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="mt-6 detail">
        <div class="d-flex align-center">
          <h3>Bonus / Fee Marketing</h3>
          <v-btn
            small
            color="primary"
            class="ml-4 no-print"
            @click="openBonusDialog()"
          >
            Tambah Bonus
          </v-btn>
        </div>
        <p v-if="state.loadingBonus"><small>Memuat bonus ...</small></p>
        <p v-else-if="!bonusList.length"><small>Belum ada bonus.</small></p>
        <table v-else width="100%" class="tbl">
          <thead>
            <tr>
              <th>Marketing</th>
              <th>Kategori</th>
              <th>Periode</th>
              <th>Tgl Dikeluarkan</th>
              <th>Jumlah TTB</th>
              <th class="text-right">Nominal</th>
              <th>Keterangan</th>
              <th class="no-print"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="b in bonusList" :key="b.id">
              <td>{{ b.nama_marketing }}</td>
              <td>{{ kategoriLabel(b.kategori) }}</td>
              <td>
                {{ formatDate(b.periode_awal) }} s/d
                {{ formatDate(b.periode_akhir) }}
              </td>
              <td>{{ formatDate(b.tgl_bonus) }}</td>
              <td class="text-center">
                {{ b.jumlah_ttb }}
                <v-btn x-small text color="primary" @click="ttbDialogBonus = b">
                  Lihat
                </v-btn>
              </td>
              <td class="text-right">{{ formatCurrency(b.nominal) }}</td>
              <td>{{ b.keterangan || "-" }}</td>
              <td class="no-print text-center">
                <v-btn x-small text color="primary" @click="openBonusDialog(b)">
                  Ubah
                </v-btn>
                <v-btn x-small text color="error" @click="deleteBonus(b)">
                  Hapus
                </v-btn>
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr>
              <td colspan="5"><b>Total Bonus</b></td>
              <td class="text-right">
                <b>{{ formatCurrency(totalBonus) }}</b>
              </td>
              <td></td>
              <td class="no-print"></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </main>

    <v-dialog v-model="bonusDialog" max-width="480" persistent>
      <v-card>
        <v-card-title>
          {{ bonusForm.id ? "Ubah Bonus" : "Tambah Bonus" }}
        </v-card-title>
        <v-card-text>
          <p class="mb-3">
            Periode: {{ formatDate(reportPeriode.awal) }} s/d
            {{ formatDate(reportPeriode.akhir) }}
          </p>
          <v-autocomplete
            v-model="bonusForm.marketing"
            :items="marketingOnlyOptions"
            :disabled="!!bonusForm.id"
            label="Marketing"
            outlined
            dense
          ></v-autocomplete>
          <v-select
            v-model="bonusForm.kategori"
            :items="groups.map(g => ({ text: g.label, value: g.key }))"
            :disabled="!!bonusForm.id"
            label="Kategori Pelunasan"
            outlined
            dense
          ></v-select>
          <p v-if="!bonusForm.id" class="mb-3">
            TTB yang ikut dikeluarkan insentifnya: <b>{{ bonusTtbs.length }}</b>
            (Tagihan {{ formatCurrency(bonusTtbTagihan) }}). TTB ini tidak akan
            muncul lagi di laporan "Belum Dikeluarkan".
          </p>
          <v-alert
            v-if="
              !bonusForm.id &&
                bonusForm.marketing &&
                bonusForm.kategori &&
                bonusTtbs.length === 0
            "
            type="warning"
            dense
            text
          >
            Tidak ada TTB marketing ini di kategori tersebut yang belum
            dikeluarkan bonusnya pada laporan yang sedang ditampilkan. Tampilkan
            laporan untuk marketing ini dengan status "Belum Dikeluarkan" terlebih
            dahulu.
          </v-alert>
          <v-text-field
            v-model="nominalDisplay"
            inputmode="numeric"
            prefix="Rp"
            label="Nominal Bonus/Fee"
            outlined
            dense
          ></v-text-field>
          <v-text-field
            v-model="bonusForm.tgl_bonus"
            type="date"
            label="Tanggal Dikeluarkan"
            outlined
            dense
          ></v-text-field>
          <v-textarea
            v-model="bonusForm.keterangan"
            label="Keterangan"
            rows="2"
            outlined
            dense
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text :disabled="state.savingBonus" @click="bonusDialog = false">
            Batal
          </v-btn>
          <v-btn
            color="primary"
            :loading="state.savingBonus"
            :disabled="!bonusValid"
            @click="saveBonus"
          >
            Simpan
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      :value="!!ttbDialogBonus"
      max-width="420"
      @input="ttbDialogBonus = null"
    >
      <v-card v-if="ttbDialogBonus">
        <v-card-title>TTB Insentif {{ ttbDialogBonus.nama_marketing }}</v-card-title>
        <v-card-text>
          <p class="mb-2">
            Dikeluarkan: {{ formatDate(ttbDialogBonus.tgl_bonus) }}<br />
            {{ kategoriLabel(ttbDialogBonus.kategori) }}
          </p>
          <ul>
            <li v-for="t in ttbDialogBonus.ttb" :key="t.kodettb">
              {{ t.nottb }}
            </li>
          </ul>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="ttbDialogBonus = null">Tutup</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-btn
      class="mx-2 no-print"
      fab
      dark
      right
      bottom
      fixed
      color="success"
      @click="printDocument"
    >
      Print
    </v-btn>
  </div>
</template>

<script>
import axios from "axios";

const pad = n => String(n).padStart(2, "0");
const ymd = (y, m, d) => `${y}-${pad(m)}-${pad(d)}`;

export default {
  data() {
    const now = new Date();
    return {
      state: {
        loading: false,
        loadingMarketing: false,
        loadingBonus: false,
        savingBonus: false,
        noData: true,
        searched: false
      },
      periodType: "bulan",
      periodTypes: [
        { text: "Per Bulan", value: "bulan" },
        { text: "Per Triwulan", value: "triwulan" },
        { text: "Rentang Tanggal", value: "rentang" }
      ],
      triwulanOptions: [
        { text: "Triwulan 1 (Jan - Mar)", value: 1 },
        { text: "Triwulan 2 (Apr - Jun)", value: 2 },
        { text: "Triwulan 3 (Jul - Sep)", value: 3 },
        { text: "Triwulan 4 (Okt - Des)", value: 4 }
      ],
      bulan: `${now.getFullYear()}-${pad(now.getMonth() + 1)}`,
      triwulan: Math.floor(now.getMonth() / 3) + 1,
      tahun: now.getFullYear(),
      rentangAwal: "",
      rentangAkhir: "",
      formData: { marketing: "", status_bonus: "belum" },
      statusBonusOptions: [
        { text: "Belum Dikeluarkan", value: "belum" },
        { text: "Sudah Dikeluarkan", value: "sudah" },
        { text: "Semua", value: "semua" }
      ],
      reportStatusBonus: "belum",
      ttbDialogBonus: null,
      marketingOptions: [{ text: "Semua", value: "" }],
      reportMarketingName: "Semua",
      reportPeriode: { awal: "", akhir: "" },
      reportMarketing: "",
      groups: [
        { key: "lunas_kurang_1_bulan", label: "Lunas di Bawah 1 Bulan" },
        { key: "lunas_lebih_1_bulan", label: "Lunas di Atas 1 Bulan" }
      ],
      rekapRows: [
        { key: "total", label: "Total" },
        { key: "lunas_kurang_1_bulan", label: "Lunas di Bawah 1 Bulan" },
        { key: "lunas_lebih_1_bulan", label: "Lunas di Atas 1 Bulan" }
      ],
      data: [],
      bonusList: [],
      bonusDialog: false,
      bonusForm: this.emptyBonusForm()
    };
  },
  computed: {
    periode() {
      if (this.periodType === "bulan") {
        const [y, m] = (this.bulan || "").split("-").map(Number);
        if (!y || !m) return { awal: "", akhir: "" };
        return {
          awal: ymd(y, m, 1),
          akhir: ymd(y, m, new Date(y, m, 0).getDate())
        };
      }
      if (this.periodType === "triwulan") {
        const y = Number(this.tahun);
        if (!y || y < 2000 || y > 2100) return { awal: "", akhir: "" };
        const mAwal = (this.triwulan - 1) * 3 + 1;
        const mAkhir = mAwal + 2;
        return {
          awal: ymd(y, mAwal, 1),
          akhir: ymd(y, mAkhir, new Date(y, mAkhir, 0).getDate())
        };
      }
      if (
        this.rentangAwal &&
        this.rentangAkhir &&
        this.rentangAwal <= this.rentangAkhir
      ) {
        return { awal: this.rentangAwal, akhir: this.rentangAkhir };
      }
      return { awal: "", akhir: "" };
    },
    statusBonusLabel() {
      return (
        this.statusBonusOptions.find(o => o.value === this.reportStatusBonus)
          ?.text || ""
      );
    },
    showTglInsentif() {
      return this.reportStatusBonus !== "belum";
    },
    // TTB di laporan (belum dikeluarkan) yang akan ikut tercatat pada bonus baru
    bonusTtbs() {
      const f = this.bonusForm;
      if (f.id || !f.marketing || !f.kategori) return [];
      const m = this.data.find(
        x => String(x.marketing).trim() === String(f.marketing).trim()
      );
      return m
        ? m.items.filter(i => i.status === f.kategori && !i.bonus_id)
        : [];
    },
    bonusTtbTagihan() {
      return this.bonusTtbs.reduce((a, i) => a + i.tagihan, 0);
    },
    marketingOnlyOptions() {
      return this.marketingOptions.filter(o => o.value !== "");
    },
    totalBonus() {
      return this.bonusList.reduce((a, b) => a + Number(b.nominal || 0), 0);
    },
    // Tampilan nominal dengan pemisah ribuan (mis. 2.000.000), nilai tetap angka
    nominalDisplay: {
      get() {
        const n = this.bonusForm.nominal;
        return n === "" || n === null || n === undefined
          ? ""
          : new Intl.NumberFormat("id-ID").format(n);
      },
      set(v) {
        const digits = String(v || "").replace(/\D/g, "");
        this.bonusForm.nominal = digits === "" ? "" : Number(digits);
      }
    },
    bonusValid() {
      const f = this.bonusForm;
      return (
        !!f.marketing &&
        (!!f.id || (!!f.kategori && this.bonusTtbs.length > 0)) &&
        f.nominal !== "" &&
        Number(f.nominal) >= 0 &&
        !!f.tgl_bonus
      );
    }
  },
  created() {
    this.fetchMarketing();
  },
  methods: {
    emptyBonusForm() {
      return {
        id: null,
        marketing: "",
        kategori: "",
        nominal: "",
        tgl_bonus: "",
        keterangan: ""
      };
    },
    async fetchMarketing() {
      this.state.loadingMarketing = true;
      try {
        const res = await axios.get("marketing");
        this.marketingOptions = [
          { text: "Semua", value: "" },
          ...res.data
            .map(item => ({ text: item.nama, value: String(item.user_id) }))
            .sort((a, b) => a.text.localeCompare(b.text, "id"))
        ];
      } catch (err) {
        alert(err.response?.data?.message || "Gagal memuat daftar marketing");
      } finally {
        this.state.loadingMarketing = false;
      }
    },
    printDocument() {
      window.print();
    },
    fetchLaporan() {
      const { awal, akhir } = this.periode;
      if (!awal || !akhir) return;
      return this.loadLaporan(
        awal,
        akhir,
        this.formData.marketing,
        this.formData.status_bonus
      );
    },
    async loadLaporan(awal, akhir, marketing, statusBonus) {
      this.state.loading = true;
      this.state.noData = true;
      this.data = [];
      const selectedMarketing = this.marketingOptions.find(
        item => item.value === marketing
      );

      try {
        const res = await axios.get("laporan/marketing", {
          params: {
            tanggal_awal: awal,
            tanggal_akhir: akhir,
            marketing: marketing || undefined,
            status_bonus: statusBonus
          }
        });
        this.data = res.data || [];
        this.state.noData = this.data.length === 0;
        this.reportMarketingName = selectedMarketing?.text || "Semua";
        this.reportPeriode = { awal, akhir };
        this.reportMarketing = marketing;
        this.reportStatusBonus = statusBonus;
        this.state.searched = true;
      } catch (err) {
        alert(err.response?.data?.message || "Gagal memuat laporan");
        this.state.loading = false;
        return;
      }
      this.state.loading = false;
      this.fetchBonus();
    },
    async fetchBonus() {
      this.state.loadingBonus = true;
      try {
        const res = await axios.get("bonus-marketing", {
          params: {
            tanggal_awal: this.reportPeriode.awal,
            tanggal_akhir: this.reportPeriode.akhir,
            marketing: this.reportMarketing || undefined
          }
        });
        this.bonusList = res.data || [];
      } catch (err) {
        this.bonusList = [];
        alert(err.response?.data?.message || "Gagal memuat bonus");
      } finally {
        this.state.loadingBonus = false;
      }
    },
    openBonusDialog(bonus) {
      if (bonus) {
        this.bonusForm = {
          id: bonus.id,
          marketing: String(bonus.marketing ?? ""),
          kategori: bonus.kategori,
          nominal: Number(bonus.nominal),
          tgl_bonus: String(bonus.tgl_bonus).substring(0, 10),
          keterangan: bonus.keterangan || ""
        };
      } else {
        this.bonusForm = {
          ...this.emptyBonusForm(),
          marketing: this.reportMarketing ? String(this.reportMarketing) : "",
          tgl_bonus: new Date().toISOString().substring(0, 10)
        };
      }
      this.bonusDialog = true;
    },
    async saveBonus() {
      this.state.savingBonus = true;
      const f = this.bonusForm;
      const payload = {
        nominal: f.nominal,
        tgl_bonus: f.tgl_bonus,
        keterangan: f.keterangan || null
      };
      if (!f.id) {
        Object.assign(payload, {
          marketing: String(f.marketing),
          kategori: f.kategori,
          periode_awal: this.reportPeriode.awal,
          periode_akhir: this.reportPeriode.akhir,
          kodettb: this.bonusTtbs.map(i => i.kodettb)
        });
      }
      try {
        if (f.id) {
          await axios.put(`bonus-marketing/${f.id}`, payload);
        } else {
          await axios.post("bonus-marketing", payload);
        }
        this.bonusDialog = false;
        await this.reloadLaporan();
      } catch (err) {
        alert(err.response?.data?.message || "Gagal menyimpan bonus");
      } finally {
        this.state.savingBonus = false;
      }
    },
    async deleteBonus(bonus) {
      if (!confirm("Hapus data bonus ini?")) return;
      try {
        await axios.delete(`bonus-marketing/${bonus.id}`);
        await this.reloadLaporan();
      } catch (err) {
        alert(err.response?.data?.message || "Gagal menghapus bonus");
      }
    },
    reloadLaporan() {
      return this.loadLaporan(
        this.reportPeriode.awal,
        this.reportPeriode.akhir,
        this.reportMarketing,
        this.reportStatusBonus
      );
    },
    kategoriLabel(k) {
      return this.groups.find(g => g.key === k)?.label || k;
    },
    sumOf(items, field) {
      return items.reduce((acc, i) => acc + Number(i[field] || 0), 0);
    },
    itemsOf(m, status) {
      return m.items.filter(i => i.status === status);
    },
    grand(key, field) {
      return this.data.reduce((acc, m) => acc + m[key][field], 0);
    },
    formatDate(date) {
      if (!date) return "-";
      const d = new Date(date);
      if (Number.isNaN(d.getTime())) return "-";
      return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`;
    },
    formatCurrency(num) {
      return new Intl.NumberFormat("id-ID").format(Math.round(num || 0));
    }
  }
};
</script>

<style lang="css" scoped>
.report {
  background-color: white;
  width: 100%;
  font-family: times;
  padding: 20px;
  color: black;
}
.tbl {
  border-collapse: collapse;
}
.tbl th,
.tbl td {
  border: 1px solid #999;
  padding: 2px 4px;
}
.rekap {
  min-width: 50%;
}
.detail {
  page-break-inside: avoid;
}
@media print {
  .no-print {
    display: none !important;
  }
}
</style>
